import { signOutAccount } from '#pplx/frontend/apps/ask/auth/signOutAccount.js';
import { switchAccount } from '#pplx/frontend/apps/ask/auth/switchAccount.js';
import { getLinkedAccountsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/multiaccountKeys.js';
import { useLinkedAccountsQuery } from '#pplx/frontend/apps/ask/queries/user-queries/useLinkedAccountsQuery.js';
import { getActiveAccountId } from '#pplx/frontend/browser-storage/activeAccount.js';
import { toPublicWebUrl } from '#pplx/frontend/common/location.js';
import { sanitizedRedirectWindow } from '#pplx/frontend/common/sanitizedRedirectWindow.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
/**
 * The data + actions behind the account switcher (multi-account web). Wraps the linked-accounts
 * query and resolves which account this tab is acting as: the one matching the per-tab
 * `X-Pplx-Account` selection, falling back to the primary (the no-header default the backend
 * itself uses) and then the first valid account when that selection has no live cookie — e.g. it
 * was signed out in another tab. Signing out a non-active account refetches the list so the
 * switcher drops it without a manual reload; signing out *this tab's* active account switches the
 * tab to another linked account (or lands on sign-in when it was the last one) so the tab is never
 * left on a cookieless account.
 */
export function useLinkedAccounts({ reason }) {
    const queryClient = useQueryClient();
    const { data, isLoading, isError } = useLinkedAccountsQuery({ reason });
    const accounts = useMemo(() => data ?? [], [data]);
    const activeAccount = useMemo(() => {
        if (accounts.length === 0) {
            return undefined;
        }
        const activeAccountId = getActiveAccountId();
        return (accounts.find((account) => account.userId === activeAccountId) ??
            accounts.find((account) => account.isPrimary) ??
            accounts.at(0));
    }, [accounts]);
    const handleSignOutAccount = useCallback(async (accountId) => {
        // Compare against the *resolved* acting account, not the raw stored id: a tab that never
        // explicitly switched acts as the primary by fallback, so signing that primary out must
        // still take the active branch and switch rather than stranding the tab.
        const wasActiveAccount = activeAccount?.userId === accountId;
        const remainingAccounts = accounts.filter((account) => account.userId !== accountId);
        const nextAccount = remainingAccounts.find((account) => account.isPrimary) ?? remainingAccounts.at(0);
        await signOutAccount(accountId);
        if (!wasActiveAccount) {
            await queryClient.invalidateQueries({ queryKey: getLinkedAccountsQueryKey() });
            return;
        }
        // This tab was acting as the signed-out account, whose cookie is now gone; leaving the tab
        // on it would 401 (the X-Pplx-Account header no longer resolves) and drop the user to
        // sign-in. Switch to another linked account so they stay signed in; only fall through to
        // sign-in when that was the last account.
        if (nextAccount) {
            switchAccount(nextAccount.userId);
            return;
        }
        sanitizedRedirectWindow(toPublicWebUrl('/'));
    }, [accounts, activeAccount, queryClient]);
    return {
        accounts,
        activeAccount,
        isLoading,
        isError,
        switchAccount,
        signOutAccount: handleSignOutAccount,
    };
}
//# sourceMappingURL=useLinkedAccounts.js.map